//    The MIT License (MIT)
//
//    Copyright (c) 2014 YIOM
//
//    Permission is hereby granted, free of charge, to any person obtaining a copy
//    of this software and associated documentation files (the "Software"), to deal
//    in the Software without restriction, including without limitation the rights
//    to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
//    copies of the Software, and to permit persons to whom the Software is
//    furnished to do so, subject to the following conditions:
//
//    The above copyright notice and this permission notice shall be included in
//    all copies or substantial portions of the Software.
//
//    THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
//    IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
//    FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
//    AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
//    LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
//    OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
//    THE SOFTWARE.

import $ from 'jquery';

export default class Sketchpad {
    constructor(config) {
        // Enforces the context for all functions
        // for (var key in this.constructor.prototype) {
        //     this[key] = this[key].bind(this);
        // }

        // Warn the user if no DOM element was selected
        if (!config.hasOwnProperty('element')) {
            console.error('SKETCHPAD ERROR: No element selected');
            return;
        }

        if (typeof (config.element) === 'string') {
            this.element = $(config.element);
        }
        else {
            this.element = config.element;
        }

        // Width can be defined on the HTML or programatically
        this._width = config.width || this.element.attr('data-width') || 0;
        this._height = config.height || this.element.attr('data-height') || 0;

        // Pen attributes
        this.color = config.color || this.element.attr('data-color') || '#000000';
        this.penSize = config.penSize || this.element.attr('data-penSize') || 5;

        // ReadOnly sketchpads may not be modified
        this.readOnly = config.readOnly ||
            this.element.attr('data-readOnly') ||
            false;
        if (!this.readOnly) {
            this.element.css({ cursor: 'crosshair' });
        }

        // Stroke control variables
        this.strokes = config.strokes || [];
        this._currentStroke = {
            color: null,
            size: null,
            lines: [],
        };

        // Undo History
        this.undoHistory = config.undoHistory || [];
        if (config.backgroundImage) {
            this.backgroundImage = new Image();
            this.backgroundImage.src = config.backgroundImage;
            this.backgroundImage.onload = () => {
              this._drawBackground(); // draw once it's loaded
            };
          } else {
            this.backgroundImage = null;
          }
        // Animation function calls
        this.animateIds = [];

        // Set sketching state
        this._sketching = false;
        this.isArrowToolActive = false; // Add this flag to track arrow tool activation

        // Setup canvas sketching listeners
        this.reset();
    }
    //
    // Private API
    //
    _cursorPosition = (event) => {
        return {
            x: event.pageX - $(this.canvas).offset().left,
            y: event.pageY - $(this.canvas).offset().top,
        };
    }
    _draw(start, end, color, size) {
        this._stroke(start, end, color, size, 'source-over');
    }
    _erase(start, end, color, size) {
        this._stroke(start, end, color, size, 'destination-out');
    }
    _stroke(start, end, color, size, compositeOperation) {
        this.context.save();
        this.context.lineJoin = 'round';
        this.context.lineCap = 'round';
        this.context.strokeStyle = color;
        this.context.lineWidth = size;
        this.context.globalCompositeOperation = compositeOperation;
        this.context.beginPath();
        this.context.moveTo(start.x, start.y);
        this.context.lineTo(end.x, end.y);
        this.context.closePath();
        this.context.stroke();
        this.context.restore();
    }
    //
    // Callback Handlers
    //
    _mouseDown = (event) => {
        this._lastPosition = this._cursorPosition(event);
        this._currentStroke.color = this.color;
        this._currentStroke.size = this.penSize;
        this._currentStroke.lines = [];
        this._sketching = true;

        if (this.isArrowToolActive) {
            this.canvas.addEventListener('mousemove', this._mouseMoveForArrow);  // Handle mouse move separately for arrow tool
        } else {
            this.canvas.addEventListener('mousemove', this._mouseMove);
        }
    };
    _mouseUp = (event) => {
        if (this._sketching) {
            if (this.isArrowToolActive) {
                var currentPosition = this._cursorPosition(event);
                this._drawArrow(this._lastPosition, currentPosition);
                this.strokes.push({
                    color: this.color,
                    size: this.penSize,
                    lines: [{ start: this._lastPosition, end: currentPosition, arrow: true }]  // Save the arrow stroke
                });
                this.canvas.removeEventListener('mousemove', this._mouseMoveForArrow); // Stop listening for arrow moves
            } else {
                this.strokes.push($.extend(true, {}, this._currentStroke));
                this.canvas.removeEventListener('mousemove', this._mouseMove);
            }
            this._sketching = false;
        }
    };
    _mouseMove = (event) => {
        var currentPosition = this._cursorPosition(event);

        this._draw(this._lastPosition, currentPosition, this.color, this.penSize);
        this._currentStroke.lines.push({
            start: $.extend(true, {}, this._lastPosition),
            end: $.extend(true, {}, currentPosition),
        });

        this._lastPosition = currentPosition;
    }
    _touchStart = (event) => {
        event.preventDefault();
        if (this._sketching) {
            return;
        }
        this._lastPosition = this._cursorPosition(event.changedTouches[0]);
        this._currentStroke.color = this.color;
        this._currentStroke.size = this.penSize;
        this._currentStroke.lines = [];
        this._sketching = true;
        this.canvas.addEventListener('touchmove', this._touchMove, false);
    }
    _touchEnd = (event) => {
        event.preventDefault();
        if (this._sketching) {
            this.strokes.push($.extend(true, {}, this._currentStroke));
            this._sketching = false;
        }
        this.canvas.removeEventListener('touchmove', this._touchMove);
    }
    _touchCancel = (event) => {
        event.preventDefault();
        if (this._sketching) {
            this.strokes.push($.extend(true, {}, this._currentStroke));
            this._sketching = false;
        }
        this.canvas.removeEventListener('touchmove', this._touchMove);
    }
    _touchLeave = (event) => {
        event.preventDefault();
        if (this._sketching) {
            this.strokes.push($.extend(true, {}, this._currentStroke));
            this._sketching = false;
        }
        this.canvas.removeEventListener('touchmove', this._touchMove);
    }
    _touchMove = (event) => {
        event.preventDefault();
        var currentPosition = this._cursorPosition(event.changedTouches[0]);

        this._draw(this._lastPosition, currentPosition, this.color, this.penSize);
        this._currentStroke.lines.push({
            start: $.extend(true, {}, this._lastPosition),
            end: $.extend(true, {}, currentPosition),
        });

        this._lastPosition = currentPosition;
    }
    //
    // Public API
    //
    reset() {
        // Set attributes
        if(!this.element[0]){
            return false
          }
        this.canvas = this.element[0];
        this.canvas.width = this._width;
        this.canvas.height = this._height;
        this.context = this.canvas.getContext('2d');

        // Setup event listeners
        this.redraw(this.strokes);

        if (this.readOnly) {
            return;
        }

        // Mouse
        this.canvas.addEventListener('mousedown', this._mouseDown);
        this.canvas.addEventListener('mouseout', this._mouseUp);
        this.canvas.addEventListener('mouseup', this._mouseUp);

        // Touch
        this.canvas.addEventListener('touchstart', this._touchStart);
        this.canvas.addEventListener('touchend', this._touchEnd);
        this.canvas.addEventListener('touchcancel', this._touchCancel);
        this.canvas.addEventListener('touchleave', this._touchLeave);
    }
    drawStroke(stroke) {
        for (let j = 0; j < stroke.lines.length; j++) {
            const line = stroke.lines[j];
            if (line.arrow) {
                // If the stroke is an arrow, draw it as an arrow
                this._drawArrow(line.start, line.end);
            } else {
                // Otherwise, it's a normal stroke
                this._draw(line.start, line.end, stroke.color, stroke.size);
            }
        }
    }
    redraw(strokes) {
        for (var i = 0; i < strokes.length; i++) {
            this.drawStroke(strokes[i]);
        }
    }
    toObject() {
        return {
            width: this.canvas.width,
            height: this.canvas.height,
            strokes: this.strokes,
            undoHistory: this.undoHistory,
        };
    }
    toJSON() {
        return JSON.stringify(this.toObject());
    }
    animate(ms, loop, loopDelay) {
        this.clear();
        var delay = ms;
        var callback = null;
        for (var i = 0; i < this.strokes.length; i++) {
            var stroke = this.strokes[i];
            for (var j = 0; j < stroke.lines.length; j++) {
                var line = stroke.lines[j];
                callback = this._draw.bind(this, line.start, line.end,
                    stroke.color, stroke.size);
                this.animateIds.push(setTimeout(callback, delay));
                delay += ms;
            }
        }
        if (loop) {
            loopDelay = loopDelay || 0;
            callback = this.animate.bind(this, ms, loop, loopDelay);
            this.animateIds.push(setTimeout(callback, delay + loopDelay));
        }
    }
    cancelAnimation() {
        for (var i = 0; i < this.animateIds.length; i++) {
            clearTimeout(this.animateIds[i]);
        }
    }
    _drawBackground() {
        if (this.backgroundImage && this.backgroundImage.complete) {
          const img = this.backgroundImage;
          const canvas = this.canvas;
          const ctx = this.context;
      
          // Clear the canvas before drawing
          ctx.clearRect(0, 0, canvas.width, canvas.height);
      
          // Calculate aspect ratios
          const imgAspect = img.width / img.height;
          const canvasAspect = canvas.width / canvas.height;
      
          let drawWidth, drawHeight, offsetX, offsetY;
      
          if (imgAspect > canvasAspect) {
            // Image is wider — fit width, crop top/bottom
            drawWidth = canvas.width;
            drawHeight = canvas.width / imgAspect;
            offsetX = 0;
            offsetY = (canvas.height - drawHeight) / 2;
          } else {
            // Image is taller — fit height, crop sides
            drawHeight = canvas.height;
            drawWidth = canvas.height * imgAspect;
            offsetX = (canvas.width - drawWidth) / 2;
            offsetY = 0;
          }
      
          ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
        }
     }
      clear() {
        this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this._drawBackground();
       }
    undo() {
        const stroke = this.strokes.pop();
        if (stroke) {
          this.undoHistory.push(stroke);
        }
        this.clear();
        this.redraw(this.strokes);
    }
    redo() {
        const stroke = this.undoHistory.pop();
        if (stroke) {
          this.strokes.push(stroke);
        }
        this.clear();
        this.redraw(this.strokes);
    }
    _drawArrow = (start, end) => {
        this.context.save();
        this.context.strokeStyle = this.color;
        this.context.lineWidth = this.penSize;

        // Draw the main line of the arrow
        this.context.beginPath();
        this.context.moveTo(start.x, start.y);
        this.context.lineTo(end.x, end.y);
        this.context.stroke();

        // Calculate the arrowhead (up and down lines)
        const headLength = 10; // Length of the arrowhead
        const angle = Math.atan2(end.y - start.y, end.x - start.x);

        // Up arrowhead
        this.context.beginPath();
        this.context.moveTo(end.x, end.y);
        this.context.lineTo(end.x - headLength * Math.cos(angle - Math.PI / 6),
            end.y - headLength * Math.sin(angle - Math.PI / 6));
        this.context.stroke();

        // Down arrowhead
        this.context.beginPath();
        this.context.moveTo(end.x, end.y);
        this.context.lineTo(end.x - headLength * Math.cos(angle + Math.PI / 6),
            end.y - headLength * Math.sin(angle + Math.PI / 6));
        this.context.stroke();

        this.context.restore();
    };

    // Mouse move handler for Arrow Tool to allow dynamic arrow drawing while moving the mouse
    // _mouseMoveForArrow = (event) => {
    //     const currentPosition = this._cursorPosition(event);

    //     // Redraw everything before drawing the arrow to avoid overlapping
    //     // this.clear();
    //     this.redraw(this.strokes);

    //     // Draw the dynamic arrow
    //     this._drawArrow(this._lastPosition, currentPosition);
    // };

}